/**
 * The Store for the App
 */

import { Permissions } from './models/permissions';
import { get, writable, readable, derived } from 'svelte/store';
import { txt } from './i18n/i18n';

let newId = -1;

export const antiForgery = writable();

export const store = writable();

export const settings = writable();

export function getNewId() {
    return newId--;
}

/**
 * The survey object
 */
export const survey = writable({ surveyInstanceId: null, isCreated: false });

/**
 * The current index in the questionIds array that user is working on
 */
export const currentQuestionIndex = writable(0);

/**
 * @param {Array.<Question>} questions, all questions for the survey
 */
export const questions = writable([]);

/**
 * Array of all questionIds (in sequence)
 */
export const questionSequence = derived(questions, $questions => $questions.map(q => q.questionId));

/**
 * @param {Array.<QuestionAnswer>} questionAnswers, these are the answers for all questions during answering a survey
 */
export const questionAnswers = writable([]);

/**
 * Store for the navigation
 *
 * @param {String} page Current page ('View', 'Edit', 'ViewAnswer', 'EditQuestion', 'EditQuestionList')
 */
export const navigation = writable({ page: 'View' });

/**
  * @returns {boolean} returns true if the "Take survey" functionality is in 'Preview' mode.
 */
export function isPreviewMode() { return get(navigation).takeMode === 'Preview'; }

export const respondentsPageState = writable({ search: '', filter: 'Completed' });

// TODO just use the Permissions class directly, instead of wrapping in a store, will never change
export let permissions = readable({ canRead: false, canParticipate: false, canEvaluate: false, canModify: false });

/**
 * The time of the browser in a variable that will be updated
*/
export const time = readable(new Date(), function start(set) {
    const interval = setInterval(() => {
        set(new Date());
    }, 10000);

    return function stop() {
        clearInterval(interval);
    };
});

const gracePeriodInMinutes = 0;

/**
 * If the survey is inactive. This does not affect users with modify or modifyInstance permission. 5 minutes grace period added.
*/
export const isInActiveForRespondents = derived(time, $time => {
    let inActive = false;

    const nowWithGrace = new Date($time);

    nowWithGrace.setMinutes(nowWithGrace.getMinutes() - gracePeriodInMinutes);

    if (activeFrom > $time || activeTo < nowWithGrace) {
        inActive = !get(permissions).canModify && !get(permissions).canModifyInstance;
    }

    return inActive;
});

/**
 * If the survey is inactive.
*/
export const isInActive = derived(time, $time => {
    return (activeFrom > $time || activeTo < $time);
});

/**
 * If the surveys deadline has passed.
*/
export const hasDeadlinePassed = derived(time, $time => {
    const hasDeadline = deadline > new Date('0001-01-01T00:00:00Z');

    return (hasDeadline && deadline < $time && !allowSubmitAfterDeadline);
});

/**
 * If the surveys deadline has passed. This does not affect users with modify or modifyInstance permission. 5 minutes grace period added.
*/
export const hasDeadlinePassedForRespondents = derived(time, $time => {
    let deadlinePassed = false;

    const nowWithGrace = new Date($time);
    const hasDeadline = deadline > new Date('0001-01-01T00:00:00Z');

    nowWithGrace.setMinutes(nowWithGrace.getMinutes() - gracePeriodInMinutes);

    if (hasDeadline && deadline < nowWithGrace && !allowSubmitAfterDeadline) {
        deadlinePassed = !get(permissions).canModify && !get(permissions).canModifyInstance;
    }

    return deadlinePassed;
});

/**
 * The language of the user
*/
export let language = 'en-GB';

/**
 * The locale of the user used with time and date
*/
export let locale = 'en-GB';

/**
 * If the users uses 12 hour time format
*/
export let use12HTimeFormat = false;

/**
 * The time zone of the user in Olson format
*/
export let olsonTimeZoneId = 'Europe/London';

/**
 * The title of the survey
*/
export let surveyTitle = '';

/**
 * @param {Object} texts for the RelativeDate component
 */
export let i18nRelativeDate;

/**
 * @param {Array.<Answer>} allAnswers, these are all (full) answers currently retrieved.
 */
export const allAnswers = writable([]);

/**
 * @param {Date} deadline. Deadline for the survey
 */
export let deadline;

let activeFrom;
let activeTo;
let allowSubmitAfterDeadline;

/** Reset a survey store and question answers store object. Call this when deleting the answer */
export function resetSurveyAnswer() {
    survey.update(currentSurvey => {
        currentSurvey.answerId = 0;
        currentSurvey.answeredQuestionIds = [];
        currentSurvey.completed = '0001-01-01T00:00:00';
        currentSurvey.started = '0001-01-01T00:00:00';

        return currentSurvey;
    });
    questionAnswers.set([]);
}

export function storeInit(config) {
    store.set(config);

    permissions = readable(new Permissions(config.permissions));
    language = config.language;
    use12HTimeFormat = config.use12HTimeFormat;
    locale = config.locale;
    olsonTimeZoneId = config.olsonTimeZoneId;
    deadline = new Date(config.deadline);
    activeFrom = new Date(config.activeFrom);
    activeTo = new Date(config.activeTo);
    allowSubmitAfterDeadline = config.allowSubmitAfterDeadline;
    surveyTitle = config.title || txt('Survey_title');

    // must be set after language has been configured, since txt method is called
    i18nRelativeDate = {
        oneDayAgo: txt('DateFormat_yesterday_at_x'),
        nDaysAgo: txt('DateFormat_x_days_ago'),
        week: txt('DateFormat_weekday_x_at_y'),
        oneHourAgo: txt('DateFormat_one_hour_ago'),
        nHoursAgo: txt('DateFormat_x_hours_ago'),
        oneMinuteAgo: txt('DateFormat_one_minute_ago'),
        nMinutesAgo: txt('DateFormat_x_minutes_ago'),
        justNow: txt('DateFormat_just_now'),

        day0: txt('Sunday'),
        day1: txt('Monday'),
        day2: txt('Tuesday'),
        day3: txt('Wednesday'),
        day4: txt('Thursday'),
        day5: txt('Friday'),
        day6: txt('Saturday')
    };
}
